import { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ValidationError } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations';
import { ComponentValidationError } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { Option } from 'dashboard/components/FormControls/SelectField';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Select from 'design-system/components/Select';
import { Label, Paragraph } from 'design-system/components/Text';
import { UiTransitionComponentConfig } from 'lib/interfaces/ui-components';
import { useFormSecurely } from 'lib/security/reactHookForm';

const createTargetOption = (step: InquiryTemplateVersionStep) => (
  <Option key={step.name} value={step.name}>
    {getDisplayNameForStep(step)}
  </Option>
);

enum TransitionSettingType {
  WORKFLOW = 'use-workflow',
  STEP = 'use-step',
}

const emptyFormReset = {
  'workflow-version-id': null,
  'workflow-on-error-step-name': null,
  'to-step': null,
};

export const MISSING_WORKFLOW_ACTION_MSG =
  'Select an action sequence and action sequence error screen for transition';
export const MISSING_WORKFLOW_ON_ERROR_STEP_MSG =
  'Select a fallback error screen for the selected action sequence';
export const MISSING_TO_STEP_MSG = 'Select a screen for transition';

type TransitionSettingProps = {
  componentConfig: UiTransitionComponentConfig['config'];
  steps: InquiryTemplateVersionStep[];
  workflowVersionMap: WorkflowVersionMap;
  editable: boolean;
  componentValidationErrors: ComponentValidationError[] | undefined;
  isNewComponent: boolean;
  updatePathPrefix: string | undefined;
  currentStep: InquiryTemplateVersionStepUi;
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
};

const TransitionSetting = ({
  componentConfig,
  steps,
  workflowVersionMap,
  editable,
  componentValidationErrors,
  isNewComponent,
  currentStep,
  updatePathPrefix,
  handleUpdateComponentConfig,
}: TransitionSettingProps) => {
  const {
    'workflow-version-id': initialWorkflowVersionId,
    'workflow-on-error-step-name': initialWorkflowOnErrorStepName,
    'to-step': initialToStep,
  } = componentConfig;

  const defaultValues = {
    'workflow-version-id': initialWorkflowVersionId,
    'workflow-on-error-step-name': initialWorkflowOnErrorStepName,
    'to-step': initialToStep,
  };

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormSecurely<{
    'workflow-version-id': null | string;
    'workflow-on-error-step-name': null | string;
    'to-step': null | string;
  }>({
    defaultValues: defaultValues,
  });

  const handleSubmitForm = handleSubmit((data) => {
    handleUpdateComponentConfig({
      [updatePathPrefix ? `${updatePathPrefix}.workflow-version-id` : 'workflow-version-id']:
        data['workflow-version-id'],
      [updatePathPrefix
        ? `${updatePathPrefix}.workflow-on-error-step-name`
        : 'workflow-on-error-step-name']: data['workflow-on-error-step-name'],
      [updatePathPrefix ? `${updatePathPrefix}.to-step` : 'to-step']: data['to-step'],
    });
  });

  useEffect(() => reset(defaultValues), [componentConfig, reset]);

  // default to step if no selection is made
  const defaultTransitionSettingForComponent = useMemo(
    () =>
      componentConfig['workflow-version-id'] != null
        ? TransitionSettingType.WORKFLOW
        : TransitionSettingType.STEP,
    [componentConfig],
  );

  const [transitionStepSetting, setTransitionStepSetting] = useState<TransitionSettingType>(
    defaultTransitionSettingForComponent,
  );

  useEffect(() => {
    setTransitionStepSetting(defaultTransitionSettingForComponent);
  }, [defaultTransitionSettingForComponent]);

  const workflowVersions = Object.values(workflowVersionMap);
  const hasWorkflowVersionsAvailable = workflowVersions.length > 0;

  const invalidTransitionValue = componentValidationErrors?.find(
    (error) => error.type === ValidationError.TransitionInvalid,
  );

  const missingWorkflowErrorStep = componentValidationErrors?.find(
    (error) => error.type === ValidationError.TransitionActionMissingErrorStep,
  );

  const onTransitionTypeChange = (event: TransitionSettingType) => {
    setTransitionStepSetting(event);
    reset(emptyFormReset);
    handleSubmitForm();
  };

  useEffect(() => {
    if (transitionStepSetting === TransitionSettingType.WORKFLOW) {
      if (invalidTransitionValue)
        setError('workflow-version-id', { type: 'custom', message: MISSING_WORKFLOW_ACTION_MSG });
      if (missingWorkflowErrorStep)
        setError('workflow-on-error-step-name', {
          type: 'custom',
          message: MISSING_WORKFLOW_ON_ERROR_STEP_MSG,
        });
    } else if (invalidTransitionValue) {
      setError('to-step', { type: 'custom', message: MISSING_TO_STEP_MSG });
    }
  }, [missingWorkflowErrorStep, invalidTransitionValue, transitionStepSetting]);

  /**
   * We only want to enable updating the transition options for components they are new components
   * (not already saved on the template)
   *
   * This is because by changing the transition step or action, users can unintentionally
   * orphan a screen or an entire branch within an inquiry flow.
   * At this time, we don't expose the concept of orphaned nodes within the editor, so
   * this is a stop-gap from doing so indirectly.
   */
  const enableTransitionOptionsForComponent = useMemo(
    () =>
      isNewComponent
        ? true
        : !(componentConfig['to-step'] != null || componentConfig['workflow-version-id'] != null),
    [componentConfig, isNewComponent],
  );

  const transitionOptionDisabled = !editable || !enableTransitionOptionsForComponent;

  return (
    <>
      {hasWorkflowVersionsAvailable && (
        <>
          <Label weight='bold'>
            Transition type
            <HelpIcon>
              <Paragraph size='sm'>
                This sets the screen to transition to a different screen, or to use an action
                sequence that determines the next screen.
              </Paragraph>
            </HelpIcon>
          </Label>
          <RadioGroup
            id='transition-type-setting-group'
            value={transitionStepSetting}
            onChange={onTransitionTypeChange}
            horizontal
          >
            <Radio
              label='Screen'
              value={TransitionSettingType.STEP}
              disabled={transitionOptionDisabled}
            />
            <Radio
              label='Action Sequence'
              value={TransitionSettingType.WORKFLOW}
              disabled={transitionOptionDisabled}
            />
          </RadioGroup>
        </>
      )}
      {transitionStepSetting === TransitionSettingType.WORKFLOW && (
        <>
          <Label weight='bold'>
            Action Sequence
            <HelpIcon>
              {/*TODO: update the wording of this to be more clear */}
              <Paragraph size='sm'>
                Action sequences can run logic within an inquiry to determine which screen to route
                the user to based on customizable conditions.
              </Paragraph>
            </HelpIcon>
          </Label>
          <Controller
            name='workflow-version-id'
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                id='workflow-action-select-field'
                placeholder='Select an action sequence'
                disabled={transitionOptionDisabled}
                value={value}
                hasError={invalidTransitionValue != null}
                onValueChange={(value) => {
                  onChange(value);
                  clearErrors('workflow-version-id');
                  handleSubmitForm();
                }}
              >
                {workflowVersions.map(
                  ({ id, attributes: { description, 'workflow-name': workflowName } }) => (
                    <Option key={id} value={id}>
                      {description || workflowName || id}
                    </Option>
                  ),
                )}
              </Select>
            )}
          />
          {errors['workflow-version-id'] && (
            <FormError>{errors['workflow-version-id'].message}</FormError>
          )}
          <Label weight='bold'>
            Action sequence error screen
            <HelpIcon>
              <Paragraph size='sm'>
                If your action sequence fails for any reason, users will be routed to this screen.
              </Paragraph>
            </HelpIcon>
          </Label>
          <Controller
            name='workflow-on-error-step-name'
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                id='workflow-action-error-step-name-select'
                placeholder='Select action sequence error screen'
                disabled={transitionOptionDisabled}
                hasError={missingWorkflowErrorStep != null}
                value={value}
                onValueChange={(value) => {
                  onChange(value);
                  clearErrors('workflow-on-error-step-name');
                  handleSubmitForm();
                }}
              >
                {steps.map(createTargetOption)}
              </Select>
            )}
          />
          {errors['workflow-on-error-step-name'] && (
            <FormError>{errors['workflow-on-error-step-name'].message}</FormError>
          )}
        </>
      )}
      {(transitionStepSetting === TransitionSettingType.STEP || !hasWorkflowVersionsAvailable) && (
        <>
          <Label weight='bold'>Screen</Label>
          <Controller
            name='to-step'
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                id='transition-screen-select'
                placeholder='Select transition screen'
                disabled={transitionOptionDisabled}
                hasError={invalidTransitionValue != null}
                value={value}
                onValueChange={(value) => {
                  onChange(value);
                  clearErrors('to-step');
                  handleSubmitForm();
                }}
              >
                {steps.filter((step) => step.name !== currentStep.name).map(createTargetOption)}
              </Select>
            )}
          />
          {errors['to-step'] && <FormError>{errors['to-step'].message}</FormError>}
        </>
      )}
    </>
  );
};

export default TransitionSetting;
